/* ======================================================================
   Cuivre hair & scalp assessment — flow, scoring and results.
   Structure mirrors the Fleur quiz: 10 base questions, two conditional
   follow-ups, two interstitials, email capture, analysing, results.
   Cuivre sells one product, so the routine section recommends bloom at
   the right supply length rather than inventing accessories.
   ====================================================================== */
(function () {
  'use strict';

  var ICON_CHECK = '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 8.6l3.2 3.2L13 5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ICON_BACK = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15 5l-7 7 7 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ICON_CART = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 8h12l-1 11H7L6 8z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M9.5 8V6.5a2.5 2.5 0 015 0V8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
  var ICON_TICK = '<svg class="hq-claim-icon" viewBox="0 0 18 18" fill="none" aria-hidden="true"><circle cx="9" cy="9" r="8" stroke="currentColor" stroke-width="1.2" opacity="0.55"/><path d="M5.4 9.3l2.4 2.4L12.6 6.9" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var STEPS = [
    { id: 'intro', type: 'intro' },

    { id: 'concern', type: 'multi', max: 3,
      title: 'what brought you here?',
      sub: 'Pick up to three.',
      options: [
        { id: 'thinning', label: 'My hair is thinning' },
        { id: 'shedding', label: 'I’m shedding more than usual' },
        { id: 'breakage', label: 'It breaks and feels dry' },
        { id: 'flat', label: 'It’s flat and lifeless' },
        { id: 'scalp', label: 'My scalp is itchy or flaky' },
        { id: 'edges', label: 'My hairline is receding' }
      ] },

    { id: 'pattern', type: 'single',
      showIf: function (a) { return has(a.concern, ['thinning', 'shedding', 'edges']); },
      title: 'where do you notice it most?',
      options: [
        { id: 'part', label: 'A widening part' },
        { id: 'crown', label: 'The crown' },
        { id: 'temples', label: 'Temples and hairline' },
        { id: 'diffuse', label: 'Evenly, all over' },
        { id: 'patches', label: 'Distinct patches' }
      ] },

    { id: 'duration', type: 'single',
      title: 'how long has this been going on?',
      options: [
        { id: 'acute', label: 'Under 3 months' },
        { id: 'subacute', label: '3 to 12 months' },
        { id: 'chronic', label: '1 to 3 years' },
        { id: 'longstanding', label: 'Over 3 years' }
      ] },

    { id: 'lifestage', type: 'multi',
      title: 'has anything changed lately?',
      sub: 'Select everything that applies.',
      exclusive: 'none',
      options: [
        { id: 'postpartum', label: 'I had a baby' },
        { id: 'menopause', label: 'Perimenopause or menopause' },
        { id: 'stress', label: 'High stress, illness or surgery' },
        { id: 'diet', label: 'A big change in diet or weight' },
        { id: 'medical', label: 'A medication or medical condition' },
        { id: 'hereditary', label: 'Thinning runs in my family' },
        { id: 'none', label: 'None of these' }
      ] },

    { id: 'postpartum_when', type: 'single',
      showIf: function (a) { return has(a.lifestage, ['postpartum']); },
      title: 'how long ago?',
      options: [
        { id: 'pp_0_3', label: '0–3 months' },
        { id: 'pp_3_6', label: '3–6 months' },
        { id: 'pp_6_12', label: '6–12 months' },
        { id: 'pp_over', label: 'Over a year' }
      ] },

    { id: 'menopause_stage', type: 'single',
      showIf: function (a) { return has(a.lifestage, ['menopause']); },
      title: 'which stage?',
      options: [
        { id: 'peri', label: 'Perimenopause' },
        { id: 'meno', label: 'Menopause' },
        { id: 'post', label: 'Post-menopause' }
      ] },

    { id: 'science_2', type: 'interstitial',
      eyebrow: 'the formula',
      title: 'meet bloom.',
      body: 'Six copper peptides — including GHK-Cu and AHK-Cu — in one lightweight daily serum.',
      claims: [
        'Visibly fuller, thicker-looking hair',
        'Daily use, any hair type',
        'Lightweight — no wash day needed'
      ],
      cta: 'Continue' },

    { id: 'hairtype', type: 'single', grid: true,
      title: 'how would you describe your hair?',
      options: [
        { id: 'straight', label: 'Straight' },
        { id: 'wavy', label: 'Wavy' },
        { id: 'curly', label: 'Curly' },
        { id: 'coily', label: 'Coily' }
      ] },

    { id: 'texture', type: 'single', grid: true,
      title: 'and each strand is…',
      options: [
        { id: 'fine', label: 'Fine' },
        { id: 'medium', label: 'Medium' },
        { id: 'coarse', label: 'Coarse' },
        { id: 'unsure', label: 'Not sure' }
      ] },

    { id: 'scalp_state', type: 'multi',
      title: 'what’s your scalp like?',
      exclusive: 'comfortable',
      options: [
        { id: 'oily', label: 'Oily, or product builds up' },
        { id: 'dry', label: 'Dry or tight' },
        { id: 'flaky', label: 'Flaky or itchy' },
        { id: 'sensitive', label: 'Sensitive' },
        { id: 'comfortable', label: 'Comfortable' }
      ] },

    { id: 'damage', type: 'multi',
      title: 'any of these in your routine?',
      exclusive: 'none_dmg',
      options: [
        { id: 'heat', label: 'Heat styling' },
        { id: 'colour', label: 'Colour, highlights or bleach' },
        { id: 'chemical', label: 'Relaxer, perm or keratin' },
        { id: 'tension', label: 'Tight styles or extensions' },
        { id: 'none_dmg', label: 'None of these' }
      ] },

    { id: 'treatments', type: 'single',
      title: 'what have you tried?',
      options: [
        { id: 'none_t', label: 'Nothing yet' },
        { id: 'otc', label: 'Shampoos or supplements' },
        { id: 'rx', label: 'Prescription treatment' },
        { id: 'most', label: 'Most things — nothing stuck' }
      ] },

    { id: 'commitment', type: 'single',
      title: 'how much time do you have?',
      options: [
        { id: 'minimal', label: 'Under a minute a day' },
        { id: 'few', label: 'A few minutes a day' },
        { id: 'whatever', label: 'Whatever works' }
      ] },

    { id: 'science_3', type: 'interstitial',
      eyebrow: 'what to expect',
      title: 'hair moves on its own schedule.',
      body: 'Follicles cycle slowly. Here is the honest shape of it:',
      timeline: [
        { when: 'Month 1', what: 'Scalp settles', why: 'Less itch and tightness.' },
        { when: 'Month 2', what: 'Shedding eases', why: 'Fewer strands in the brush.' },
        { when: 'Month 3', what: 'Baby hairs appear', why: 'New growth along the part and temples.' },
        { when: 'Months 4–6', what: 'Density builds', why: 'Those new hairs thicken and lengthen.' }
      ],
      cta: 'Continue' },

    { id: 'email', type: 'email' },
    { id: 'analysing', type: 'analysing' },
    { id: 'results', type: 'results' }
  ];

  var TICKER = [
    'Reading your answers…',
    'Matching your scalp profile…',
    'Checking what your hair type needs…',
    'Ruling out what won’t suit you…',
    'Ordering your routine…'
  ];

  function has(list, ids) {
    if (!list) return false;
    for (var i = 0; i < ids.length; i++) { if (list.indexOf(ids[i]) !== -1) return true; }
    return false;
  }

  /* ---- diagnosis ------------------------------------------------------ */

  var DIAGNOSIS = {
    postpartum: {
      lead: 'What you’re seeing has a name: postpartum telogen effluvium. During pregnancy, raised oestrogen holds hairs in their growth phase far longer than usual. After birth those hormones drop and all of that held hair leaves at once.',
      detail: function (a) {
        if (a.postpartum_when === 'pp_0_3') {
          return 'At this stage you may be just ahead of the peak, which typically lands around month three. It is temporary — but the routine you build now is what determines how densely it grows back.';
        }
        if (a.postpartum_when === 'pp_3_6' || a.postpartum_when === 'pp_6_12') {
          return 'You’re in the window where shedding usually starts to settle and regrowth begins. Those short, fine baby hairs along your hairline are new growth, not breakage — supporting them now is the whole game.';
        }
        return 'Past twelve months, most postpartum shedding has resolved on its own. If density hasn’t returned, it’s worth treating this as an ongoing pattern rather than waiting it out.';
      }
    },
    menopause: {
      lead: 'Falling oestrogen shortens the growth phase of each hair and shrinks the follicle a little with every cycle. The result is gradual, diffuse thinning that shows up first where the hair parts.',
      detail: function () {
        return 'This one doesn’t resolve by itself, which is exactly why a consistent daily scalp routine matters more here than at any other life stage.';
      }
    },
    telogen: {
      lead: 'A physical stressor — illness, sustained stress, a sharp change in diet — can push a large share of follicles out of their growth phase at once. The shed usually shows up two to three months after the event, which is why it so often feels like it came from nowhere.',
      detail: function () {
        return 'The encouraging part: these follicles are dormant, not gone. A supportive scalp environment is what shortens the gap before they cycle back in.';
      }
    },
    hereditary: {
      lead: 'A family history combined with where you’re noticing it points towards a gradual, inherited pattern. Follicles miniaturise over successive cycles, producing finer and shorter hairs each time until they stop altogether.',
      detail: function () {
        return 'Consistency is everything with this pattern. It responds far better to daily support started early than to intensive effort started late.';
      }
    },
    medical: {
      lead: 'Thyroid conditions, PCOS and a number of common medications all affect the hair cycle directly. That makes the underlying condition the first thing to address — worth raising with your doctor if you haven’t.',
      detail: function () {
        return 'Alongside that, a daily scalp routine supports the environment your follicles are trying to recover in. The two work together rather than in place of each other.';
      }
    },
    general: {
      lead: 'Nothing in your answers points to a single obvious trigger, which is common — and usually means several smaller loads are stacking up: daily mechanical stress, scalp condition, and the ordinary slow drift of the hair cycle.',
      detail: function () {
        return 'That’s a workable position. It means the routine below is targeting real, addressable inputs rather than fighting one dominant cause.';
      }
    }
  };

  var MECH = [
    { test: function (a) { return has(a.damage, ['chemical']); },
      text: 'Relaxers, perms and keratin services permanently alter the cuticle, so the hair you already have needs protecting as much as new growth needs encouraging.' },
    { test: function (a) { return has(a.damage, ['heat', 'colour']); },
      text: 'Regular heat and colour weaken the strand, which means some of what reads as thinning is length lost to breakage rather than density lost at the root.' },
    { test: function (a) { return has(a.damage, ['tension']); },
      text: 'Tight styles and extensions pull constantly at the hairline. Over time that tension alone can thin the edges, independently of anything hormonal.' },
    { test: function (a) { return has(a.scalp_state, ['flaky']); },
      text: 'An itchy, flaking scalp is an inflamed one, and inflammation at the follicle interferes with growth. Calming it is a prerequisite, not an extra.' },
    { test: function (a) { return has(a.scalp_state, ['oily']); },
      text: 'Oil and product buildup sit between anything you apply and the skin you are applying it to — the most common reason a good serum underperforms.' }
  ];

  function persona(a) {
    if (has(a.lifestage, ['postpartum'])) return 'postpartum';
    if (has(a.lifestage, ['menopause'])) return 'menopause';
    if (has(a.lifestage, ['medical'])) return 'medical';
    if (has(a.lifestage, ['stress', 'diet'])) return 'telogen';
    if (has(a.lifestage, ['hereditary'])) return 'hereditary';
    return 'general';
  }

  /* Why bloom, in their words rather than ours. */
  function serumWhy(a, who) {
    if (who === 'postpartum') {
      return 'Postpartum shedding happens when a wave of follicles leaves their growth phase at once. Bloom supports the scalp through that reset so regrowth comes back in denser.';
    }
    if (who === 'menopause') {
      return 'As oestrogen falls, hairs spend less time growing before they shed. Bloom’s copper peptides support the scalp environment that longer growth phases depend on.';
    }
    if (a.pattern === 'part' || a.pattern === 'crown') {
      return 'A widening part is the classic early signal of miniaturising follicles at the crown. Bloom is formulated for exactly this — applied daily, directly to the part line.';
    }
    if (a.pattern === 'temples' || has(a.concern, ['edges'])) {
      return 'Hairline and temple follicles are the smallest and the first to struggle. Bloom goes on those edges daily, where lighter products never make contact.';
    }
    if (a.duration === 'acute' || a.duration === 'subacute') {
      return 'You’re early enough that this is worth taking seriously now. Follicles respond far better before miniaturisation sets in.';
    }
    return 'Six copper peptides, applied daily to the scalp rather than the lengths — the part of the routine that acts where hair is actually made.';
  }

  /* How to use it, tuned to hair type, scalp and how much time they have. */
  function routineSteps(a) {
    var steps = [];
    var coily = a.hairtype === 'curly' || a.hairtype === 'coily';
    steps.push({
      what: coily ? 'Part and place' : 'Straight to the scalp',
      why: coily
        ? 'Part in sections and run the dropper along each parting. Getting bloom onto scalp — not curls — is the whole job.'
        : 'Half a dropper along the part line and anywhere you’ve noticed thinning. It’s the scalp that needs it, not the lengths.'
    });
    steps.push({
      what: 'Thirty seconds of massage',
      why: has(a.scalp_state, ['oily'])
        ? 'Work it in with fingertips. It also lifts the buildup that stops most serums ever reaching skin.'
        : 'Work it in with fingertips rather than nails. It spreads the serum and wakes up circulation at the follicle.'
    });
    steps.push({
      what: a.commitment === 'minimal' ? 'Once a day, every day' : 'Daily, then leave it alone',
      why: 'No wash-out, no wash day needed. Consistency beats intensity — the follicle cycle is measured in months, not weeks.'
    });
    return steps;
  }

  /* Supply length: match the shape of the problem, not the biggest box. */
  function suggestPack() {
    /* The quiz always lands on the annual plan: it is the best value on the
       ladder and it matches the length of trial the product actually needs. */
    return 'annual';
  }

  function packReason(a, who, term) {
    if (term === 'onetime') {
      return 'One bottle, no subscription \u2014 fine if you would rather try it before committing to anything.';
    }
    if (term === 'monthly') {
      return 'Month to month, cancel whenever you like. The flexible option, at a middling rate.';
    }
    return 'Growth cycles turn slowly, and the honest length of a fair trial is a full year of consistent use \u2014 this is the plan built for that, at the lowest monthly rate we offer.';
  }

  function readback(a) {
    var out = [];
    var m = {
      thinning: 'thinning', shedding: 'shedding', breakage: 'breakage',
      flat: 'flat and lifeless', scalp: 'scalp discomfort', edges: 'receding hairline',
      part: 'at the part', crown: 'at the crown', temples: 'temples and hairline',
      diffuse: 'all over', patches: 'in patches',
      acute: 'under 3 months', subacute: '3–12 months', chronic: '1–3 years', longstanding: 'over 3 years',
      straight: 'straight', wavy: 'wavy', curly: 'curly', coily: 'coily',
      fine: 'fine strands', medium: 'medium strands', coarse: 'coarse strands'
    };
    (a.concern || []).slice(0, 2).forEach(function (c) { if (m[c]) out.push(m[c]); });
    if (m[a.pattern]) out.push(m[a.pattern]);
    if (m[a.duration]) out.push(m[a.duration]);
    if (m[a.hairtype]) out.push(m[a.hairtype]);
    if (m[a.texture]) out.push(m[a.texture]);
    return out;
  }

  /* ---- engine --------------------------------------------------------- */

  function boot(root) {
    var cfgEl = root.querySelector('[data-hq-config]');
    var cfg = {};
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { cfg = {}; }

    var stage = root.querySelector('.hq-stage');
    var backBtn = root.querySelector('[data-hq-back]');
    var countEl = root.querySelector('[data-hq-count]');
    var barEl = root.querySelector('[data-hq-progress]');

    var answers = {};
    var idx = 0;
    var email = '';

    function plan() {
      return STEPS.filter(function (s) { return !s.showIf || s.showIf(answers); });
    }
    function questions(p) {
      return p.filter(function (s) {
        return s.type === 'single' || s.type === 'multi';
      });
    }

    function money(cents) {
      var n = (cents / 100);
      var s = n.toFixed(2).replace('.', ',');
      return (cfg.currency_symbol || '€') + s;
    }

    function esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    function syncRail() {
      var p = plan();
      var step = p[idx];
      var qs = questions(p);
      var done = 0;
      for (var i = 0; i < qs.length; i++) {
        if (p.indexOf(qs[i]) < idx) done++;
      }
      var isQ = step && (step.type === 'single' || step.type === 'multi');
      var current = isQ ? done + 1 : done;

      if (step && step.type === 'intro') {
        countEl.innerHTML = '';
        barEl.style.width = '0%';
      } else if (step && (step.type === 'results')) {
        countEl.innerHTML = '<b>' + qs.length + '</b> / ' + qs.length;
        barEl.style.width = '100%';
      } else {
        countEl.innerHTML = '<b>' + Math.max(1, current) + '</b> / ' + qs.length;
        barEl.style.width = (Math.max(0, Math.min(1, done / qs.length)) * 100) + '%';
      }
      backBtn.hidden = idx === 0 || (step && (step.type === 'analysing' || step.type === 'results'));
    }

    function paint(html) {
      root.setAttribute('data-bleed', '1');
      window.setTimeout(function () {
        stage.innerHTML = html;
        root.removeAttribute('data-bleed');
        var sc = stage.querySelector('.hq-screen');
        if (sc) sc.setAttribute('data-enter', '');
        window.scrollTo(0, 0);
        wire();
        syncRail();
      }, 170);
    }

    function go(n) {
      var p = plan();
      idx = Math.max(0, Math.min(p.length - 1, n));
      render();
    }
    function next() { go(idx + 1); }
    function back() { go(idx - 1); }

    /* ---- screen builders ---------------------------------------------- */

    function optionsHTML(step) {
      var picked = answers[step.id];
      var multi = step.type === 'multi';
      return step.options.map(function (o) {
        var on = multi ? (picked || []).indexOf(o.id) !== -1 : picked === o.id;
        var state = multi
          ? 'aria-pressed="' + (on ? 'true' : 'false') + '"'
          : 'role="radio" aria-checked="' + (on ? 'true' : 'false') + '"';
        return '<button type="button" class="hq-opt" ' + state + ' data-hq-opt="' + o.id + '">' +
          '<span class="hq-opt__check">' + ICON_CHECK + '</span><span>' + esc(o.label) + '</span></button>';
      }).join('');
    }

    function questionHTML(step) {
      var multi = step.type === 'multi';
      var grid = step.grid ? ' hq-options--grid' : '';
      var group = multi ? '' : ' role="radiogroup"';
      return '<div class="hq-screen">' +
        '<h1 class="hq-h">' + esc(step.title) + '</h1>' +
        (step.sub ? '<p class="hq-sub">' + esc(step.sub) + '</p>' : '') +
        '<div class="hq-options' + grid + '"' + group + ' data-hq-options>' + optionsHTML(step) + '</div>' +
        '<p class="hq-hint" data-hq-hint role="status" data-show="false"></p>' +
        '<div class="hq-actions">' +
          '<button type="button" class="hq-btn" data-hq-next aria-disabled="true">Continue</button>' +
        '</div></div>';
    }

    function introHTML() {
      return '<div class="hq-screen hq-screen--intro">' +
        '<span class="hq-eyebrow">Hair &amp; scalp assessment</span>' +
        '<h1 class="hq-h hq-h--lg">' + esc(cfg.intro_title) + '</h1>' +
        '<p class="hq-sub">' + esc(cfg.intro_sub) + '</p>' +
        '<div class="hq-actions"><button type="button" class="hq-btn hq-btn--light" data-hq-next>' + esc(cfg.intro_cta) + '</button></div>' +
        '<ul class="hq-intro__meta"><li><b>2 min</b>10 questions</li><li><b>Free</b>No account</li></ul>' +
        '</div>';
    }

    function interstitialHTML(step) {
      var body = '';
      if (step.claims) {
        body = '<ul class="hq-claims">' + step.claims.map(function (c) {
          return '<li>' + ICON_TICK + '<span>' + esc(c) + '</span></li>';
        }).join('') + '</ul>';
      }
      if (step.timeline) {
        body = '<ul class="hq-timeline">' + step.timeline.map(function (t) {
          return '<li><span class="hq-timeline__when">' + esc(t.when) + '</span>' +
            '<span class="hq-timeline__what">' + esc(t.what) + '</span>' +
            '<span class="hq-timeline__why">' + esc(t.why) + '</span></li>';
        }).join('') + '</ul>';
      }
      return '<div class="hq-screen hq-screen--dark">' +
        '<span class="hq-eyebrow">' + esc(step.eyebrow) + '</span>' +
        '<h1 class="hq-h">' + esc(step.title) + '</h1>' +
        '<p class="hq-sub">' + esc(step.body) + '</p>' + body +
        '<div class="hq-actions"><button type="button" class="hq-btn hq-btn--light" data-hq-next>' + esc(step.cta) + '</button></div>' +
        '</div>';
    }

    function emailHTML() {
      return '<div class="hq-screen">' +
        '<h1 class="hq-h">where should we send your routine?</h1>' +
        '<p class="hq-sub">So you can come back to it any time.</p>' +
        '<div class="hq-field">' +
          '<label class="hq-visually-hidden" for="hq-email">Email address</label>' +
          '<input class="hq-input" id="hq-email" type="email" name="email" inputmode="email" autocomplete="email" placeholder="you@example.com" data-hq-email>' +
          '<span class="hq-error" data-hq-email-error hidden></span>' +
        '</div>' +
        '<div class="hq-actions">' +
          '<button type="button" class="hq-btn hq-btn--light" data-hq-email-submit>See my routine</button>' +
          '<button type="button" class="hq-skip" data-hq-next>Skip — just show me the results</button>' +
        '</div>' +
        '<p class="hq-consent">' + esc(cfg.consent_text) + '</p>' +
        '</div>';
    }

    function analysingHTML() {
      return '<div class="hq-screen hq-screen--analysing">' +
        '<div class="hq-spinner" aria-hidden="true"></div>' +
        '<h1 class="hq-h">building your routine…</h1>' +
        '<p class="hq-ticker" data-hq-ticker aria-live="polite">' + esc(TICKER[0]) + '</p>' +
        '</div>';
    }

    function resultsHTML() {
      var who = persona(answers);
      var d = DIAGNOSIS[who] || DIAGNOSIS.general;
      var chips = readback(answers);
      var mech = MECH.filter(function (m) { return m.test(answers); }).map(function (m) { return m.text; });
      var packs = (cfg.packs || []).filter(function (x) { return x.available !== false; });
      var pick = suggestPack(answers, who);
      var lead = packs.filter(function (p) { return p.term === pick; })[0] || packs[packs.length - 1];
      if (!lead) return '<div class="hq-screen"><h1 class="hq-h">' + esc(cfg.results_title) + '</h1><p class="hq-sub">Your routine is bloom, applied daily to the scalp.</p></div>';

      var steps = routineSteps(answers);
      var fmt = function (pk, key, cents) { return pk[key] || money(cents); };
      var priceHTML = fmt(lead, 'price_formatted', lead.price) +
        (lead.compare_at && lead.compare_at > lead.price
          ? ' <s>' + fmt(lead, 'compare_at_formatted', lead.compare_at) + '</s>' : '');

      var TERM_TAG = {
        onetime: 'One bottle, no subscription',
        monthly: 'Billed monthly, cancel anytime',
        annual: '12-month term, billed monthly'
      };
      var planRows = packs.map(function (p) {
        var save = (p.compare_at && p.compare_at > p.price)
          ? Math.ceil((1 - p.price / p.compare_at) * 100) : 0;
        var pf = p.price_formatted || money(p.price);
        return '<li class="hq-plan__row" role="radio" tabindex="0" aria-checked="' + (p.term === lead.term ? 'true' : 'false') + '" data-hq-pack="' + p.variant_id + '" data-price-formatted="' + esc(pf) + '">' +
          '<span class="hq-plan__what">' + esc(p.title) + (save > 0 ? '<span class="hq-plan__save">save ' + save + '%</span>' : '') + '</span>' +
          '<span class="hq-plan__price">' + esc(pf) +
            (p.term === 'onetime' ? '' : '<span class="hq-plan__per">a month</span>') + '</span>' +
          '<span class="hq-plan__tag">' + (TERM_TAG[p.term] || '') + '</span>' +
          '</li>';
      }).join('');

      return '<div class="hq-screen hq-screen--results">' +
        '<div class="hq-results__head">' +
          '<span class="hq-eyebrow">Your results</span>' +
          '<h1 class="hq-h hq-h--lg">' + esc(cfg.results_title) + '</h1>' +
          (chips.length ? '<ul class="hq-readback">' + chips.map(function (c) { return '<li>' + esc(c) + '</li>'; }).join('') + '</ul>' : '') +
        '</div>' +

        '<div class="hq-diagnosis">' +
          '<h2>What’s likely going on</h2>' +
          '<p>' + esc(d.lead) + '</p>' +
          '<div class="hq-diagnosis__more" data-hq-more-body hidden>' +
            '<p>' + esc(d.detail(answers)) + '</p>' +
            mech.map(function (t) { return '<p>' + esc(t) + '</p>'; }).join('') +
          '</div>' +
          '<button type="button" class="hq-more" data-hq-more aria-expanded="false">Read the full analysis</button>' +
        '</div>' +

        '<h2 class="hq-section-title">Your routine <span>1 product, 3 habits</span></h2>' +
        '<ul class="hq-recs">' +
          '<li class="hq-rec hq-rec--lead">' +
            '<div class="hq-rec__media"><img src="' + esc(cfg.product_image) + '" alt="" loading="lazy" width="160" height="160"></div>' +
            '<div class="hq-rec__body">' +
              '<span class="hq-rec__flag">Your match</span>' +
              '<h3 class="hq-rec__name">' + esc(cfg.product_title) + '</h3>' +
              '<p class="hq-rec__why">' + esc(serumWhy(answers, who)) + '</p>' +
            '</div>' +
            '<div class="hq-rec__buy">' +
              '<span class="hq-rec__price" data-hq-price>' + priceHTML + '</span>' +
              '<button type="button" class="hq-rec__add hq-rec__add--solid" data-hq-add="' + lead.variant_id + '">Add</button>' +
            '</div>' +
          '</li>' +
        '</ul>' +

        '<h2 class="hq-section-title">How to use it <span>about 30 seconds</span></h2>' +
        '<ul class="hq-steps">' + steps.map(function (s) {
          return '<li><b>' + esc(s.what) + '</b><span>' + esc(s.why) + '</span></li>';
        }).join('') + '</ul>' +

        '<section class="hq-supply">' +
          '<h2>give it a <em>full cycle</em>.</h2>' +
          '<p class="hq-supply__lede">' + esc(packReason(answers, who, lead.term)) + '</p>' +
          '<ul class="hq-plan" role="radiogroup" aria-label="Supply length">' + planRows + '</ul>' +
          '<p class="hq-sub-note">Free delivery on every order. Changing the supply length above updates what gets added to your cart.</p>' +
        '</section>' +

        '<div class="hq-bundle">' +
          '<button type="button" class="hq-btn hq-btn--light" data-hq-add="' + lead.variant_id + '" data-hq-primary>Add to cart</button>' +
          '<button type="button" class="hq-bundle__cart" data-hq-cart-open>View my cart</button>' +
        '</div>' +

        '<p class="hq-restart">Answered something wrong? <button type="button" data-hq-restart>Retake the quiz</button></p>' +
        '<p class="hq-disclaimer">' + esc(cfg.disclaimer) + '</p>' +
        '</div>';
    }

    function render() {
      var step = plan()[idx];
      if (!step) return;
      if (step.type === 'intro') return paint(introHTML());
      if (step.type === 'interstitial') return paint(interstitialHTML(step));
      if (step.type === 'email') return paint(emailHTML());
      if (step.type === 'analysing') {
        paint(analysingHTML());
        var t = 0;
        var iv = window.setInterval(function () {
          t++;
          var el = stage.querySelector('[data-hq-ticker]');
          if (!el) { window.clearInterval(iv); return; }
          if (t >= TICKER.length) { window.clearInterval(iv); next(); return; }
          el.textContent = TICKER[t];
        }, 620);
        return;
      }
      if (step.type === 'results') return paint(resultsHTML());
      return paint(questionHTML(step));
    }

    /* ---- interaction --------------------------------------------------- */

    function answered(step) {
      var v = answers[step.id];
      if (step.type === 'multi') return !!(v && v.length);
      return !!v;
    }

    function refreshNext(step) {
      var btn = stage.querySelector('[data-hq-next]');
      if (btn) btn.setAttribute('aria-disabled', answered(step) ? 'false' : 'true');
    }

    function hint(msg) {
      var el = stage.querySelector('[data-hq-hint]');
      if (!el) return;
      el.textContent = msg || '';
      el.setAttribute('data-show', msg ? 'true' : 'false');
    }

    function choose(step, id, btn) {
      if (step.type === 'single') {
        answers[step.id] = id;
        stage.querySelectorAll('[data-hq-opt]').forEach(function (b) {
          b.setAttribute('aria-checked', b === btn ? 'true' : 'false');
        });
        hint('');
      } else {
        var cur = answers[step.id] ? answers[step.id].slice() : [];
        var at = cur.indexOf(id);
        var ex = step.exclusive;
        if (at === -1) {
          if (ex && id === ex) { cur = [id]; }
          else {
            if (ex) { cur = cur.filter(function (c) { return c !== ex; }); }
            if (step.max && cur.length >= step.max) {
              hint('Pick up to ' + step.max + ' — deselect one to swap.');
              var nb = stage.querySelector('[data-hq-next]');
              if (nb) { nb.setAttribute('data-nudge', 'true'); window.setTimeout(function () { nb.removeAttribute('data-nudge'); }, 460); }
              return;
            }
            cur.push(id);
          }
        } else {
          cur.splice(at, 1);
        }
        answers[step.id] = cur;
        stage.querySelectorAll('[data-hq-opt]').forEach(function (b) {
          b.setAttribute('aria-pressed', cur.indexOf(b.getAttribute('data-hq-opt')) !== -1 ? 'true' : 'false');
        });
        if (!step.max || cur.length < step.max) hint('');
      }
      refreshNext(step);
    }

    function addToCart(variantId, btn) {
      if (!variantId) return;
      if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
      fetch(cfg.routes_cart_add || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      }).then(function (r) { return r.json(); }).then(function () {
        if (btn) { btn.disabled = false; btn.textContent = 'Added'; btn.setAttribute('data-added', 'true'); }
        return fetch(cfg.routes_cart || '/cart.js').then(function (r) { return r.json(); });
      }).then(function (cart) {
        document.querySelectorAll('.cart-count-bubble span, [data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
        });
        var rail = root.querySelector('[data-hq-cart-count]');
        if (rail) rail.textContent = cart.item_count;
      }).catch(function () {
        if (btn) { btn.disabled = false; btn.textContent = 'Try again'; }
      });
    }

    function submitEmail(value) {
      var body = new URLSearchParams();
      body.append('form_type', 'customer');
      body.append('utf8', '✓');
      body.append('contact[email]', value);
      body.append('contact[tags]', 'hair-quiz');
      return fetch('/contact', { method: 'POST', body: body, headers: { Accept: 'text/html' } })
        .catch(function () { /* never block the results on a marketing signup */ });
    }

    function wire() {
      var step = plan()[idx];
      if (!step) return;

      stage.querySelectorAll('[data-hq-opt]').forEach(function (b) {
        b.addEventListener('click', function () { choose(step, b.getAttribute('data-hq-opt'), b); });
      });

      var nextBtn = stage.querySelector('[data-hq-next]');
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          if (nextBtn.getAttribute('aria-disabled') === 'true') {
            hint('Pick an answer to carry on.');
            nextBtn.setAttribute('data-nudge', 'true');
            window.setTimeout(function () { nextBtn.removeAttribute('data-nudge'); }, 460);
            return;
          }
          next();
        });
      }
      if (step.type === 'single' || step.type === 'multi') refreshNext(step);

      var mailBtn = stage.querySelector('[data-hq-email-submit]');
      if (mailBtn) {
        mailBtn.addEventListener('click', function () {
          var input = stage.querySelector('[data-hq-email]');
          var err = stage.querySelector('[data-hq-email-error]');
          var v = (input.value || '').trim();
          if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) {
            input.setAttribute('aria-invalid', 'true');
            err.hidden = false;
            err.textContent = 'That doesn’t look like an email address.';
            input.focus();
            return;
          }
          input.removeAttribute('aria-invalid');
          err.hidden = true;
          email = v;
          submitEmail(v);
          next();
        });
        var inp = stage.querySelector('[data-hq-email]');
        if (inp) {
          inp.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); mailBtn.click(); } });
        }
      }

      var more = stage.querySelector('[data-hq-more]');
      if (more) {
        more.addEventListener('click', function () {
          var body = stage.querySelector('[data-hq-more-body]');
          var open = more.getAttribute('aria-expanded') === 'true';
          more.setAttribute('aria-expanded', open ? 'false' : 'true');
          body.hidden = open;
          more.textContent = open ? 'Read the full analysis' : 'Show less';
        });
      }

      stage.querySelectorAll('[data-hq-add]').forEach(function (b) {
        b.addEventListener('click', function () { addToCart(b.getAttribute('data-hq-add'), b); });
      });

      stage.querySelectorAll('[data-hq-pack]').forEach(function (row) {
        function pick() {
          stage.querySelectorAll('[data-hq-pack]').forEach(function (r) { r.setAttribute('aria-checked', r === row ? 'true' : 'false'); });
          var vid = row.getAttribute('data-hq-pack');
          stage.querySelectorAll('[data-hq-add]').forEach(function (b) {
            b.setAttribute('data-hq-add', vid);
            b.removeAttribute('data-added');
            b.textContent = b.hasAttribute('data-hq-primary') ? 'Add to cart' : 'Add';
          });
          var priceEl = stage.querySelector('[data-hq-price]');
          if (priceEl) priceEl.textContent = row.getAttribute('data-price-formatted') || '';
        }
        row.addEventListener('click', pick);
        row.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); }
        });
      });

      var cartOpen = stage.querySelector('[data-hq-cart-open]');
      if (cartOpen) cartOpen.addEventListener('click', function () { window.location.href = cfg.routes_cart_page || '/cart'; });

      var restart = stage.querySelector('[data-hq-restart]');
      if (restart) {
        restart.addEventListener('click', function () { answers = {}; email = ''; go(0); });
      }
    }

    var onScroll = function () {
      root.setAttribute('data-scrolled', window.pageYOffset > 8 ? '1' : '0');
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    backBtn.innerHTML = ICON_BACK;
    backBtn.setAttribute('aria-label', 'Back');
    backBtn.addEventListener('click', back);

    var cartLink = root.querySelector('[data-hq-cart-link]');
    if (cartLink) {
      cartLink.innerHTML = ICON_CART + '<span class="hq-visually-hidden">Cart</span>';
    }

    render();
  }

  function init() {
    document.querySelectorAll('[data-hq-root]').forEach(boot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
